<template>
  <Modal
    title="Analisar pagamento"
    :active="active"
    @close="$emit('close')"
    class="payment-modal"
    data-testId="modal-anti-fraud"
  >
    <div class="wrapper" v-if="data.id">
      <div>
        <div class="flex flex-row items-center gap-1">
          <span data-testId="anti-fraud-transaction-id"
            ><strong>ID:</strong>
            {{ data.antiFraudTransaction.providerTransactionId }}
          </span>

          <div>
            <vs-button
              v-tooltip="'Copiar'"
              color="primary"
              type="flat"
              icon-pack="feather"
              icon="icon-copy"
              size="small"
              data-testId="anti-fraud-copy-id-button"
              @click="
                copyToClipboard(data.antiFraudTransaction.providerTransactionId)
              "
            ></vs-button>
          </div>

          <span class="date">
            {{ formattedDate(data.antiFraudTransaction.createdAt) }}
          </span>
        </div>

        <div class="source">
          <span class="font-bold">Fonte de dados:</span>
          {{ data.antiFraudTransaction.providerName.toUpperCase() }}
        </div>
      </div>

      <div class="flex flex-row justify-between">
        <div class="payer">
          <strong class="text-lg">Pagador:</strong><br />

          <strong>Nome:</strong>
          {{ payer.name }}<br />

          <strong>CPF/CNPJ:</strong>
          {{ payer.taxId }}<br />

          <strong>Telefone:</strong>
          {{ payer.phone || '-' }}<br />

          <strong>E-mail:</strong>
          {{ payer.email }}<br />

          <strong>Endereço:</strong>
          {{ fullAddress }}
        </div>

        <div class="risk" v-if="hasDecisionScore">
          <div class="risk-title">Risco de fraude</div>
          <div
            class="risk-score"
            :class="riskClass(data.antiFraudTransaction.decisionScore)"
            data-testId="anti-fraud-risk-score"
          >
            {{ data.antiFraudTransaction.decisionScore }}
          </div>
          <div
            class="risk-label"
            :class="riskClass(data.antiFraudTransaction.decisionScore)"
            data-testId="anti-fraud-risk-label"
          >
            {{ riskLabel }}
          </div>
        </div>
      </div>

      <div
        class="grid"
        v-if="isPessoaFisica && (hasRatings || hasInsights)"
        :class="{ 'grid-single': !hasRatings && hasInsights }"
      >
        <div class="box" v-if="hasRatings">
          <div class="box-title">Vínculo de Dados</div>

          <div class="graph">
            <svg class="links" viewBox="0 0 200 200">
              <line
                x1="100"
                y1="20"
                x2="20"
                y2="100"
                :class="linksStrength.cpfZip"
              />

              <line
                x1="100"
                y1="20"
                x2="180"
                y2="100"
                :class="linksStrength.cpfPhone"
              />

              <line
                x1="100"
                y1="20"
                x2="100"
                y2="180"
                :class="linksStrength.cpfEmail"
              />

              <line
                x1="20"
                y1="100"
                x2="180"
                y2="100"
                :class="linksStrength.phoneZip"
              />

              <line
                x1="20"
                y1="100"
                x2="100"
                y2="180"
                :class="linksStrength.emailZip"
              />

              <line
                x1="180"
                y1="100"
                x2="100"
                y2="180"
                :class="linksStrength.phoneEmail"
              />
            </svg>

            <div class="node top">
              <span>CPF</span>
            </div>
            <div class="node left">
              <span>CEP</span>
            </div>
            <div class="node right">
              <span>Celular</span>
            </div>
            <div class="node bottom">
              <span>Email</span>
            </div>
          </div>

          <div class="legend flex flex-row gap-6">
            <span class="l high">● Alto</span>
            <span class="l medium">● Médio</span>
            <span class="l low">● Baixo</span>
            <span class="l none">● Sem dados</span>
          </div>
        </div>

        <div class="box" v-if="hasInsights">
          <div class="box-title">Insights</div>

          <div class="insights">
            <div class="insight header">
              <span>Código</span>
              <span>Descrição</span>
              <span>Categoria</span>
              <span>Tipo</span>
            </div>

            <div class="insights-scroll">
              <div
                v-for="insight in insights"
                :key="insight.code"
                class="insight"
              >
                <span>{{ insight.code }}</span>
                <span>{{ insight.description }}</span>

                <span class="badge" :class="badgeClass(insight.relevance)">
                  {{ insight.relevance }}
                </span>

                <span>{{ insight.type }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ValidationObserver ref="observer">
        <div class="analysis">
          <div v-if="canEdit">
            <strong>Análise do Pagamento</strong>

            <ValidationProvider
              name="Ação"
              rules="required"
              v-slot="{ errors }"
            >
              <div class="flex flex-row gap-4 items-center">
                <v-select
                  class="action-select"
                  placeholder="Escolha uma ação"
                  :options="['Aprovar', 'Recusar']"
                  v-model="form.action"
                  :class="{ 'is-invalid': errors.length }"
                  data-testId="anti-fraud-action-select"
                >
                  <template #option="{ label }">
                    <span :data-testId="`anti-fraud-action-option-${label}`">
                      {{ label }}
                    </span>
                  </template>
                </v-select>

                <div>
                  <vs-input
                    v-tooltip="'Upload de arquivos'"
                    id="Upload de arquivos"
                    type="file"
                    hidden
                    style="display: none"
                    data-testId="anti-fraud-file-input"
                    @change="handleFiles($event.target.files)"
                  />
                  <vs-button
                    @click.prevent="chooseFiles('Upload de arquivos')"
                    icon-pack="feather"
                    icon="icon-upload"
                    data-testId="anti-fraud-upload-button"
                  />
                </div>
              </div>

              <span v-show="errors.length" class="text-danger text-sm">
                {{ errors[0] }}
              </span>
            </ValidationProvider>

            <ValidationProvider
              name="Justificativa"
              rules="required|min:5"
              v-slot="{ errors }"
            >
              <vs-textarea
                placeholder="Justifique a ação"
                class="action-textarea"
                v-model="form.comments"
                :danger="errors.length > 0"
                data-testId="anti-fraud-comments-textarea"
              />

              <span v-show="errors.length" class="text-danger text-sm">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>

          <div v-if="hasManualReview" class="payer">
            <strong class="text-lg">Análise do Pagamento:</strong><br />

            <strong>Data:</strong>
            {{ formattedDate(data.antiFraudTransaction.manualDecisionAt)
            }}<br />

            <strong>Responsável:</strong>
            {{ data.antiFraudTransaction.manualDecisionBy.name }}<br />

            <strong>Ação:</strong>
            {{ handleStatus(data.antiFraudTransaction.status) }}<br />

            <strong>Justificativa:</strong>
            {{ data.antiFraudTransaction.manualDecisionComments }}<br />
          </div>

          <template v-if="hasManualReview">
            <div
              v-for="(file, index) in existingAttachments"
              :key="'existing-' + index"
              class="attachment"
            >
              <div class="preview">
                <img
                  v-if="isImage(file)"
                  :src="filePreview(file)"
                  alt="preview"
                />

                <feather-icon v-else icon="FileText" size="32" />
              </div>

              <div class="info">
                <span class="name">{{ file.fileName }}</span>
              </div>

              <div class="actions">
                <vs-button
                  color="primary"
                  type="flat"
                  icon-pack="feather"
                  icon="icon-eye"
                  size="small"
                  data-testId="anti-fraud-view-file-button"
                  @click="openFile(file)"
                />
              </div>
            </div>
          </template>

          <div
            v-for="(file, index) in form.attachments"
            :key="'new-' + index"
            class="attachment"
          >
            <div class="preview">
              <img
                v-if="isImage(file)"
                :src="filePreview(file)"
                alt="preview"
              />

              <feather-icon v-else icon="FileText" size="32" />
            </div>

            <div class="info">
              <span class="name">{{ file.name }}</span>
              <span class="size">
                {{ (file.size / 1024 / 1024).toFixed(2) }} MB
              </span>
            </div>

            <div class="actions">
              <vs-button
                color="primary"
                type="flat"
                icon-pack="feather"
                icon="icon-eye"
                size="small"
                data-testId="anti-fraud-view-file-button"
                @click="openFile(file)"
              />

              <vs-button
                color="danger"
                type="flat"
                icon-pack="feather"
                icon="icon-trash-2"
                size="small"
                data-testId="anti-fraud-remove-file-button"
                @click="removeFile(index)"
              />
            </div>
          </div>

          <div v-if="canEdit" class="footer">
            <vs-button
              color="success"
              :disabled="submitting"
              data-testId="anti-fraud-save-button"
              @click="submitAnalysis"
            >
              Salvar
            </vs-button>
          </div>
        </div>
      </ValidationObserver>
    </div>
  </Modal>
</template>

<script>
import vSelect from 'vue-select';
import notify from '../../utils/notify';
import Modal from '../../components/Modal.vue';
import functions from '../../utils/functions';
import antiFraudApiService from '../../services/anti.fraud.api';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  components: {
    Modal,
    'v-select': vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    policyId: {
      type: Number,
      default: null,
    },
    purchaseId: {
      type: Number,
      default: null,
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      maxSize50Mb: 50 * 1024 * 1024,
      maxSizeMessage: 'O arquivos selecionados devem ter no total 50MB.',
      data: {},

      form: {
        action: null,
        comments: '',
        attachments: [],
      },

      submitting: false,

      existingAttachments: [],
    };
  },
  watch: {
    active(opened) {
      if (opened) {
        this.getData();
      } else {
        this.data = {};
      }
    },
  },
  computed: {
    payer() {
      return this.data.payer || {};
    },
    antiFraudTransaction() {
      return this.data.antiFraudTransaction || {};
    },
    isPessoaFisica() {
      return this.data && this.data.payer && this.data.payer.type === 'F';
    },
    canEdit() {
      if (this.viewOnly) return false;
      const hasPermission = this.$can('Escrita', 'Analise de Pagamento');
      const isReview = this.antiFraudTransaction.status === 'review';

      return hasPermission && isReview;
    },
    fullAddress() {
      const p = this.payer;
      return `${p.address}, ${p.number} - ${p.neighborhood}, ${p.city}/${p.state}, ${p.zipCode}`;
    },

    hasManualReview() {
      const status = this.antiFraudTransaction.status;
      return status === 'manual_approved' || status === 'manual_rejected';
    },
    hasDecisionScore() {
      const snap =
        this.data.antiFraudTransaction &&
        this.data.antiFraudTransaction.providerResponseSnapshot;
      return snap != null && snap.score != null;
    },
    hasRatings() {
      const snap =
        this.data.antiFraudTransaction &&
        this.data.antiFraudTransaction.providerResponseSnapshot;
      return snap != null && snap.ratings != null && snap.ratings.length > 0;
    },
    hasInsights() {
      const snap =
        this.data.antiFraudTransaction &&
        this.data.antiFraudTransaction.providerResponseSnapshot;
      return snap != null && snap.insights != null && snap.insights.length > 0;
    },

    insights() {
      const snap =
        this.data.antiFraudTransaction &&
        this.data.antiFraudTransaction.providerResponseSnapshot;
      return (snap && snap.insights) || [];
    },

    riskLabel() {
      const score = this.data.antiFraudTransaction.decisionScore;
      if (score <= 75) return 'Muito alto';
      if (score <= 120) return 'Alto';
      if (score <= 300) return 'Médio';
      if (score <= 600) return 'Baixo';
      if (score <= 1000) return 'Muito baixo';
      return 'Baixo risco';
    },

    linksStrength() {
      const snap =
        this.data.antiFraudTransaction &&
        this.data.antiFraudTransaction.providerResponseSnapshot;
      const ratings = (snap && snap.ratings) || [];

      const getLevel = (a, b) => {
        const r = ratings.find(
          (x) => x.relatedTo.includes(a) && x.relatedTo.includes(b),
        );

        if (!r) return 'none';
        if (r.value === 3) return 'high';
        if (r.value === 2) return 'medium';
        return 'low';
      };

      return {
        cpfPhone: getLevel('Document', 'Phone'),
        cpfEmail: getLevel('Document', 'Email'),
        cpfZip: getLevel('Document', 'ZipCode'),

        phoneEmail: getLevel('Phone', 'Email'),
        phoneZip: getLevel('Phone', 'ZipCode'),
        emailZip: getLevel('Email', 'ZipCode'),
      };
    },
  },
  methods: {
    formattedDate(dateStr) {
      if (!dateStr) return '';
      const spStr = new Date(dateStr).toLocaleString('sv-SE', {
        timeZone: 'America/Sao_Paulo',
      });
      return functions.formataDataHoraPadraoBR(spStr, true);
    },
    validateForm() {
      this.errors = {};

      if (!this.form.action) {
        this.errors.action = 'Ação é obrigatória';
      }

      if (!this.form.comments || this.form.comments.trim().length < 5) {
        this.errors.comments = 'A justificativa é obrigatória';
      }

      return Object.keys(this.errors).length === 0;
    },

    closeModal() {
      this.form.action = null;
      this.form.comments = '';
      this.form.attachments = [];

      if (this.$refs.observer) {
        this.$refs.observer.reset();
      }

      this.$emit('close');
    },
    handleFiles(file) {
      const size = file[0].size;

      if (size > this.maxSize50Mb) {
        notify.notifyFail(this.$vs, this.maxSizeMessage);
        return;
      }

      const sanitizedFileName = functions.sanitizeString(file[0].name);
      const sanitizedFile = new File([file[0]], sanitizedFileName, {
        type: file[0].type,
      });

      this.form.attachments.push(sanitizedFile);
    },
    chooseFiles(inputId) {
      document.getElementById(inputId).click();
    },
    removeFile(index) {
      this.form.attachments.splice(index, 1);
    },
    handleStatus(status) {
      if (status === 'manual_approved') return 'Aprovação Manual';
      if (status === 'manual_rejected') return 'Recusa Manual';

      return 'Em Análise';
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          notify.notifySuccess(
            this.$vs,
            'Copiado para a área de transferência.',
          );
        })
        .catch(() => {
          notify.notifyFail(this.$vs, 'Erro ao copiar');
        });
    },

    openFile(file) {
      if (file && file.fileUrl) {
        window.open(file.fileUrl, '_blank');
        return;
      }

      if (file instanceof File) {
        const url = URL.createObjectURL(file);
        window.open(url, '_blank');
      }
    },

    removeExistingAttachment(index) {
      this.existingAttachments.splice(index, 1);
    },

    badgeClass(relevance) {
      switch (relevance) {
        case 'Positivo':
          return 'positive';
        case 'Alerta':
          return 'alert';
        default:
          return 'neutral';
      }
    },

    riskClass(score) {
      if (score <= 75) return 'very-high';
      if (score <= 120) return 'high';
      if (score <= 300) return 'medium';
      if (score <= 600) return 'low';

      return 'very-low';
    },

    isImage(file) {
      if (!file) return false;
      if (file.type) {
        return file.type.startsWith('image/');
      }
      if (file.fileName) {
        return /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(file.fileName);
      }
      return false;
    },

    filePreview(file) {
      if (!file) return '';
      if (file instanceof File) {
        return URL.createObjectURL(file);
      }
      if (file.fileUrl) {
        return file.fileUrl;
      }
      return '';
    },

    async getData() {
      if (!this.purchaseId && !this.policyId) return;

      try {
        const response = this.purchaseId
          ? await antiFraudApiService.getAntiFraudReviewByPurchase(
              this.purchaseId,
            )
          : await antiFraudApiService.getAntiFraudReview(this.policyId);

        this.data = response || {};

        this.existingAttachments =
          this.data &&
          this.data.antiFraudTransaction &&
          this.data.antiFraudTransaction.attachments
            ? this.data.antiFraudTransaction.attachments
            : [];
      } catch {
        this.$vs.notify({
          position: 'top-center',
          title: 'Erro',
          text: 'Erro ao carregar dados.',
          color: 'danger',
        });

        this.$emit('close');
      }
    },

    async submitAnalysis() {
      const isValid = await this.$refs.observer.validate();

      if (!isValid) {
        notify.notifyFail(this.$vs, 'Preencha todos os campos obrigatórios.');
        return;
      }

      this.submitting = true;

      try {
        const payload = {
          policyId: this.policyId,
          action:
            this.form.action === 'Aprovar'
              ? 'manual_approved'
              : 'manual_rejected',
          comments: this.form.comments,
          antiFraudTransactionId: this.data.antiFraudTransaction.id,
          attachments: this.form.attachments,
        };

        const allFilesSize = this.form.attachments.reduce(
          (acc, file) => acc + file.size,
          0,
        );

        if (allFilesSize > this.maxSize50Mb) {
          notify.notifyFail(this.$vs, this.maxSizeMessage);
          return;
        }

        this.$vs.loading();

        await antiFraudApiService.submitManualReview(payload);

        notify.notifySuccess(this.$vs, 'Análise feita com sucesso.');
        this.closeModal();
      } catch (err) {
        notify.notifyFail(this.$vs, 'Erro ao analisar.');
      } finally {
        this.submitting = false;
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style scoped>
.wrapper {
  font-size: 13px;
  color: #5f5f5f;
}

.date {
  margin-left: 8px;
}

.source {
  font-size: 12px;
}

.risk {
  border: 1px solid #aaa;
  border-radius: 6px;
  text-align: center;
  width: 140px;
  height: fit-content;
}

.risk-title {
  background: #dedede;
  font-size: 14px;
  font-weight: 600;
  padding: 4px;
}

.risk-score {
  font-size: 34px;
  margin: 4px 0;
}

.risk-score.very-low {
  color: #2e7d32;
}

.risk-score.low {
  color: #3f51ff;
}

.risk-score.medium {
  color: #f4b400;
}

.risk-score.high {
  color: #ffa500;
}

.risk-score.very-high {
  color: #e53935;
}

.risk-label {
  font-size: 14px;
  padding-bottom: 6px;
}

.risk-label.very-low {
  color: #2e7d32;
}

.risk-label.low {
  color: #3f51ff;
}

.risk-label.medium {
  color: #f4b400;
}

.risk-label.high {
  color: #ffa500;
}

.risk-label.very-high {
  color: #e53935;
}

.payer {
  margin: 12px 0;
  line-height: 1.6;
}

.grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
}

.grid.grid-single {
  grid-template-columns: 1fr;
}

.box {
  border: 1px solid #aaa;
  border-radius: 6px;
  padding: 10px;
}

.box-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.graph {
  position: relative;
  width: 220px;
  height: 220px;
  margin: 0 auto;
}

.links {
  position: absolute;
  inset: 0;
}

.links line {
  stroke-width: 2;
}

.high {
  stroke: #3f51ff;
}

.medium {
  stroke: #f4b400;
}

.low {
  stroke: #e53935;
}

.none {
  stroke: #ccc;
}

.node {
  position: absolute;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid #aaa;
  background: #fff;
  font-size: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.node.top {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
}

.node.left {
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.node.right {
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.node.bottom {
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}

.legend {
  margin-top: 8px;
  font-size: 11px;
}

.high {
  color: #3f51ff;
}
.medium {
  color: #f4b400;
}
.low {
  color: #e53935;
}
.none {
  color: #999;
}

.insights {
  display: flex;
  flex-direction: column;
}

.insight {
  display: grid;
  grid-template-columns: 80px 1fr 90px 120px;
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.insight.header {
  font-weight: bold;
  border-bottom: 1px solid #ccc;
}

.badge {
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  min-width: 60px;
  width: fit-content;
  height: fit-content;
  text-align: center;
}

.badge.positive {
  background: #dff5e3;
  color: #2e7d32;
}

.badge.alert {
  background: #ffe5e0;
  color: #d84315;
}

.badge.neutral {
  background: #e1e1e1;
  color: #636363;
}

.analysis {
  margin-top: 16px;
}

.action-select {
  width: 100%;
  margin: 8px 0;
}

.action-textarea {
  margin-top: 8px;
}

.footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
.insights {
  display: flex;
  flex-direction: column;
}

.insights-scroll {
  max-height: 220px;
  overflow-y: auto;
}

.insights-scroll::-webkit-scrollbar {
  width: 6px;
}

.insights-scroll::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

.insights-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.attachment {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.preview {
  width: 48px;
  height: 48px;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.info {
  font-size: 12px;
}

.name {
  font-weight: 500;
  display: block;
}

.size {
  color: #777;
}
</style>

<style>
.con-vs-popup .vs-popup {
  width: 1000px !important;
}
</style>
